/**
 * **Where the control layer's credential is named** — the header a first-party surface sends a
 * Stytch session in, the body field that is the one alternative to it, the header a caller says
 * which surface it is in, and the body field a covered write carries its second factor in.
 *
 * These are here for {@link STYTCH_CLIENT_OPTIONS}'s reason, one step on again. That constant
 * is in this package because both SPAs *write* the Stytch cookie and so must agree on how it
 * is scoped. These two are here because the name of this header is now a **wire contract
 * between four codebases**: the panel and any mobile client send it, `services/api` reads it,
 * and `services/mcp`'s own `/auth/households` route reads it too. A name that four consumers
 * must spell identically, whose drift breaks no build, is precisely what `metrics.ts` already
 * establishes belongs here.
 *
 * **It was spelled twice before this**, which is the concrete reason rather than the
 * principled one. `packages/app-api/src/client.ts` declared it for the browser and
 * `services/api`'s `credential.ts` declared it for the server, and the comment on the first
 * one said *"one name, spelled once here"* — a claim that was already false when it was
 * written. Two string literals that must match, in two packages, with nothing comparing them,
 * is the shape of a bug that only ever appears at runtime and only for whoever changed one.
 *
 * **What does not move here is the reading.** `readSessionCredential` stays with the route
 * family that owns the credential, because *which* of these two a request may use, and what
 * happens when it presents both, is a policy of that surface rather than a fact about the
 * wire. This module holds the names and nothing else — not which of them a route accepts, not
 * what happens when two disagree, and not what any of them proves.
 */

/**
 * The header a first-party client puts its Stytch session JWT in.
 *
 * **Never a cookie, and that is the invariant the `/app` family exists to hold** (ADR 0021
 * decision 3). A cookie is ambient: the browser attaches it to any request to the origin,
 * including one a third-party page caused, which is what makes CSRF a category rather than a
 * bug. An explicit header is sent only by code that meant to send it.
 *
 * `x-` rather than `Authorization`, deliberately: `Authorization` on this origin is the OAuth
 * bearer token a *host* holds for `/mcp`, and one header name meaning two different
 * credentials — with two different validators and two different blast radii — is the
 * ambiguity ADR 0021 decision 3 splits the two shapes to avoid.
 */
export const SESSION_HEADER = "x-boone-session";

/**
 * The body field that carries the same session when a header cannot.
 *
 * It exists because the first three endpoints of this shape (`/auth/onboard`,
 * `/auth/create-family`, `/auth/authorize`) took the session in the body, and those callers
 * are deployed. "Explicit" is the property that matters — not "in the header" — so both are
 * accepted and neither is ambient.
 */
export const SESSION_BODY_FIELD = "sessionJwt";

/**
 * The header a first-party client puts **which surface it is** in — `mobile` from Boone's app,
 * and nothing from the panel (ADR 0025 decision 6, as amended 2026-09-13; issue #387).
 *
 * **A declaration, and the record is honest about that.** `spaHttp.ts` declined to make `web`
 * provable and said why: a session is not evidence of a browser, and nothing on the wire
 * distinguishes a phone posting a Stytch session from a laptop doing the same. That is still
 * true, so this header cannot make `mobile` *provable* either. What it does is let the caller
 * that knows say so, on a field whose whole contract is that it is a **label a caller states,
 * never a capability** (`writeChannel.ts`) — `arrivedVia` licenses nothing and no code may
 * branch on it, so a false declaration costs a family one wrong word on their own audit row,
 * written by their own session, about their own device. The alternative was recording `web`
 * for every write from every phone, which is a *known* untruth on every row rather than a
 * *possible* one on a row somebody spoofed against themselves.
 *
 * **It is deliberately not in the panel's CORS allowance.** `router.ts`'s policy names
 * `content-type` and {@link SESSION_HEADER} and nothing else, so a page on a first-party origin
 * cannot preflight a claim to be a phone. A native client sends no `Origin` and needs no
 * allowance — which is the one property of the wire that does separate the two, and it points
 * the same way the declaration does.
 *
 * The accepted values are {@link FIRST_PARTY_CHANNELS} in `writeChannel.ts`, so a further
 * first-party surface cannot arrive without that total `Record` being answered for.
 */
export const CLIENT_HEADER = "x-boone-client";

/**
 * The body field a covered write carries its **second factor** in (issue #307).
 *
 * Here for the same reason the two names above are: it is a wire contract between the panel's
 * client (`packages/app-api`) and the service that reads it (`services/api`), spelled in two
 * packages with nothing comparing the copies. That is the exact shape the header note describes
 * as *"a bug that only ever appears at runtime and only for whoever changed one"*.
 *
 * **In the body rather than a header, and stripped in the same breath as {@link
 * SESSION_BODY_FIELD}.** `services/api`'s `writes.ts` removes both before a tool sees its
 * arguments, and it removes them by name rather than relying on a schema to drop what it does
 * not declare — because a one-time code is a live credential exactly as a session JWT is, and a
 * tool that ever accepted a passthrough object would put it inside a `PROPOSAL#` stored
 * verbatim and an `ACTION#` kept for ninety days. One line closes that for every route at once;
 * a schema's default closes it per schema, silently, and only while nobody changes one.
 *
 * A header would have kept it structurally out of the body, and was rejected for splitting one
 * proof across two transports: the only caller posts JSON, and the code and the `method_id`
 * that says which send it answers are one object or they are nothing.
 */
export const STEP_UP_BODY_FIELD = "stepUp";
